import { TextField } from '@/components/ui/TextField';
import { Toggle } from '@/components/ui/Toggle';
import { Badge } from '@/components/ui/Badge';
import { SettingsCard } from './fields';
import type { SettingsSectionProps } from './fields';

const HEX_COLOR_PATTERN = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;

export function getPortalTitleError(value: string) {
  if (!value) {
    return 'Portal title is required';
  }
  if (value.length < 5) {
    return 'Portal title must be at least 5 characters';
  }
  return undefined;
}

export function getPortalLogoUrlError(value: string) {
  if (!value) {
    return undefined;
  }
  try {
    // eslint-disable-next-line no-new
    new URL(value);
    return undefined;
  } catch {
    return 'Enter a valid logo URL';
  }
}

export function getPortalColorError(value: string) {
  if (!HEX_COLOR_PATTERN.test(value)) {
    return 'Enter a valid hex colour, e.g. #1A1A1A';
  }
  return undefined;
}

export function BrandingSection({ settings, onChange }: SettingsSectionProps) {
  const portalTitle = settings.portalTitle ?? '';
  const portalLogoUrl = settings.portalLogoUrl ?? '';
  const portalPrimaryColor = settings.portalPrimaryColor ?? '#1A1A1A';
  const portalAccentColor = settings.portalAccentColor ?? '#4F46E5';

  return (
    <div className="flex flex-col gap-4">
      <SettingsCard title="Go live">
        <div className="flex items-start justify-between gap-3">
          <Toggle
            label="Portal is live"
            description="When off, the portal runs in test mode: returns are stamped test and pickups are stubbed."
            checked={settings.isLive}
            onChange={(checked) => onChange({ isLive: checked })}
          />
          <Badge tone={settings.isLive ? 'success' : 'pending'}>{settings.isLive ? 'Live' : 'Test mode'}</Badge>
        </div>
      </SettingsCard>

      <SettingsCard title="Portal branding" description="Shown on the customer returns portal.">
        <TextField
          label="Portal title"
          requiredMark
          maxLength={50}
          minLength={5}
          value={portalTitle}
          onChange={(event) => onChange({ portalTitle: event.target.value })}
          errorText={getPortalTitleError(portalTitle)}
        />
        <TextField
          label="Portal subtitle(optional)"
          maxLength={50}
          value={settings.portalSubtitle ?? ''}
          onChange={(event) => onChange({ portalSubtitle: event.target.value })}
        />
        <TextField
          label="Logo URL(optional)"
          value={portalLogoUrl}
          onChange={(event) => onChange({ portalLogoUrl: event.target.value })}
          errorText={getPortalLogoUrlError(portalLogoUrl)}
        />
        <div className="grid grid-cols-2 gap-4">
          <ColorField
            label="Primary colour"
            value={portalPrimaryColor}
            onChange={(value) => onChange({ portalPrimaryColor: value })}
            errorText={getPortalColorError(portalPrimaryColor)}
          />
          <ColorField
            label="Accent colour"
            value={portalAccentColor}
            onChange={(value) => onChange({ portalAccentColor: value })}
            errorText={getPortalColorError(portalAccentColor)}
          />
        </div>
        {/* <TextField
          label="Brand Name"
          placeholder="Shiprocket & Co"
          value={settings.customDomain ?? ''}
          onChange={(event) => onChange({ customDomain: event.target.value })}
        /> */}
      </SettingsCard>
    </div>
  );
}

function ColorField({
  label,
  value,
  onChange,
  errorText,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  errorText?: string;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-sm font-medium text-ink">{label}</label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={HEX_COLOR_PATTERN.test(value) ? value : '#000000'}
          onChange={(event) => onChange(event.target.value)}
          className="h-10 w-12 cursor-pointer rounded-lg border border-line bg-surface"
          aria-label={label}
        />
        <input
          type="text"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          aria-invalid={errorText ? true : undefined}
          className={`h-10 flex-1 rounded-lg border bg-surface px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand ${
            errorText ? 'border-danger' : 'border-line'
          }`}
        />
      </div>
      {errorText ? <p className="text-xs text-danger">{errorText}</p> : null}
    </div>
  );
}
